import React, { useState } from "react";
import { Link } from "react-router-dom";
import { MapPin, Phone, Mail, Clock, Send, CalendarDays, Headphones, ArrowUpRight, Check, Star } from "lucide-react";
import { toast } from "sonner";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import Reveal from "../components/site/Reveal";
import { SectorMap } from "../components/site/CommunesMap";
import ConsentCheck from "../components/site/ConsentCheck";
import { api, errMsg } from "../lib/api";
import { PageHero, LogoTitle } from "../components/site/Common";
import { waLink, WhatsAppIcon } from "../components/site/WhatsAppButton";
import { CONTACT, CITIES, slugify } from "../mock";

const SUBJECTS = ["Je suis propriétaire", "Je suis voyageur", "Partenariat", "Recrutement", "Autre"];
const tel = `tel:${CONTACT.phone.replace(/\s/g, "")}`;

export default function Contact() {
  const empty = { name: "", email: "", phone: "", subject: "Je suis propriétaire", city: "", message: "" };
  const [f, setF] = useState(empty);
  const [sent, setSent] = useState(false);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const owner = f.subject === "Je suis propriétaire";

  const [consent, setConsent] = useState(false);
  const [sending, setSending] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (!f.name.trim() || !f.message.trim()) return toast.error("Merci de remplir votre nom et votre message");
    if (!/\S+@\S+\.\S+/.test(f.email)) return toast.error("Adresse email invalide");
    if (!consent) return toast.error("Merci de cocher la case de consentement");
    setSending(true);
    try {
      await api.message({ ...f, consent: true });
      toast.success("Message envoyé ! Nous vous répondons rapidement.");
      setSent(true);
      setF(empty);
      setConsent(false);
    } catch (err) { toast.error(errMsg(err)); } finally { setSending(false); }
  };

  const quick = [
    { icon: Phone, k: "Appelez-nous", v: CONTACT.phone, sub: "Lun - Sam, 10h - 18h", href: tel, cls: "bg-white text-[#13261c] ring-1 ring-[#e6dcc8] hover:bg-[#f6f1e7]", ic: "bg-[#f1ebdd] text-[#c4703f]", id: "phone" },
    { icon: WhatsAppIcon, k: "WhatsApp", v: "Écrivez-nous", sub: "Lun - Sam, 10h - 18h", href: waLink(), ext: true, cls: "bg-[#25D366] text-white hover:bg-[#1ebe5b]", ic: "bg-white/20 text-white", id: "whatsapp" },
    { icon: CalendarDays, k: "Rendez-vous", v: "Réserver un créneau", sub: "Visio, téléphone ou visite", to: "/rendez-vous", cls: "bg-[#13261c] text-[#faf7f0] hover:bg-[#24402f]", ic: "bg-white/10 text-[#d9895a]", id: "rdv" },
    { icon: Mail, k: "Email", v: CONTACT.email, sub: "Réponse sous 24h ouvrées", href: `mailto:${CONTACT.email}`, cls: "bg-white text-[#13261c] ring-1 ring-[#e6dcc8] hover:bg-[#f6f1e7]", ic: "bg-[#f1ebdd] text-[#c4703f]", id: "email" },
  ];

  return (
    <div data-testid="contact-page">
      <PageHero kicker="Contact" title="parlons de" script="votre logement" text="Une question, un projet ? Notre équipe vit sur le secteur et vous répond rapidement, par le moyen qui vous convient." />

      <section className="pb-16">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {quick.map((q, i) => {
            const inner = (
              <>
                <span className={`w-12 h-12 rounded-2xl grid place-items-center shrink-0 ${q.ic}`}><q.icon className="w-6 h-6" /></span>
                <span className="block min-w-0 sm:mt-5">
                  <span className="block text-sm font-semibold opacity-75">{q.k}</span>
                  <span className={`block font-bold leading-tight [overflow-wrap:anywhere] ${q.id === "email" ? "text-[15px]" : "text-lg"}`}>{q.v}</span>
                  <span className="block mt-1 text-sm opacity-70">{q.sub}</span>
                </span>
                <ArrowUpRight className="absolute top-6 right-6 w-5 h-5 opacity-40 group-hover:opacity-100 group-hover:-translate-y-0.5 group-hover:translate-x-0.5 transition-[opacity,transform]" />
              </>
            );
            const cls = `group relative flex items-center gap-4 sm:block h-full rounded-[28px] p-5 sm:p-6 transition-colors ${q.cls}`;
            return (
              <Reveal key={q.id} delay={i * 80} className="h-full">
                {q.to ? <Link to={q.to} className={cls} data-testid={`contact-quick-${q.id}`}>{inner}</Link>
                  : <a href={q.href} target={q.ext ? "_blank" : undefined} rel={q.ext ? "noopener noreferrer" : undefined} className={cls} data-testid={`contact-quick-${q.id}`}>{inner}</a>}
              </Reveal>
            );
          })}
        </div>
        <Reveal className="max-w-7xl mx-auto px-5 lg:px-8 mt-4">
          <a href={CONTACT.google} target="_blank" rel="noopener noreferrer" data-testid="contact-google-review" className="group flex flex-wrap items-center justify-between gap-4 rounded-[28px] bg-[#f6e3d5] hover:bg-[#f1d6c3] p-5 sm:p-6 transition-colors">
            <span className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4">
              <span className="flex gap-0.5">{[0, 1, 2, 3, 4].map((i) => <Star key={i} className="w-5 h-5 fill-[#c4703f] text-[#c4703f]" />)}</span>
              <span className="font-bold text-[#13261c]">Vous avez travaillé ou séjourné avec nous ? Laissez-nous un avis sur Google</span>
            </span>
            <span className="inline-flex items-center gap-2 font-bold text-[#c4703f]">Donner mon avis<ArrowUpRight className="w-5 h-5 group-hover:-translate-y-0.5 group-hover:translate-x-0.5 transition-transform" /></span>
          </a>
        </Reveal>
      </section>

      <section className="pb-24">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 grid lg:grid-cols-12 gap-8">
          <Reveal className="lg:col-span-7 order-2 lg:order-1">
            <form onSubmit={submit} className="bg-white rounded-[36px] p-6 sm:p-10 shadow-[0_40px_90px_-40px_rgba(19,38,28,0.5)] ring-1 ring-[#13261c]/5" data-testid="contact-form">
              <LogoTitle as="h2" size="md" title="envoyez-nous" script="un message" />
              <p className="mt-2 text-[#5a6158]">Les champs marqués d'un * sont obligatoires. Vos données servent uniquement à vous répondre (<Link to="/confidentialite" className="underline">en savoir plus</Link>).</p>
              {sent && <p className="mt-4 flex items-center gap-2 rounded-2xl bg-[#f1ebdd] text-[#13261c] px-4 py-3 font-semibold" data-testid="contact-success"><Check className="w-5 h-5 text-[#c4703f]" />Merci ! Votre message a bien été envoyé.</p>}
              <div className="mt-6 flex flex-wrap gap-2" data-testid="contact-subjects">
                {SUBJECTS.map((s) => (
                  <button type="button" key={s} onClick={() => set("subject", s)} data-testid={`contact-subject-${slugify(s)}`}
                    className={`rounded-full px-4 py-2 text-sm font-bold ring-1 transition-colors ${f.subject === s ? "bg-[#13261c] text-white ring-[#13261c]" : "ring-[#e6dcc8] text-[#2c3a31] hover:bg-[#f1ebdd]"}`}>{s}</button>
                ))}
              </div>
              <div className="mt-5 grid sm:grid-cols-2 gap-3">
                <Input value={f.name} onChange={(e) => set("name", e.target.value)} placeholder="Nom et prénom *" className="h-14 rounded-2xl text-base" data-testid="contact-name" />
                <Input value={f.email} onChange={(e) => set("email", e.target.value)} placeholder="Email *" className="h-14 rounded-2xl text-base" data-testid="contact-email" />
                <Input value={f.phone} onChange={(e) => set("phone", e.target.value)} placeholder="Téléphone" className="h-14 rounded-2xl text-base" data-testid="contact-phone" />
                {owner ? (
                  <Select value={f.city} onValueChange={(v) => set("city", v)}>
                    <SelectTrigger className="h-14 rounded-2xl text-base" data-testid="contact-city"><SelectValue placeholder="Commune du logement" /></SelectTrigger>
                    <SelectContent className="max-h-72">{CITIES.map((c) => <SelectItem key={c.name} value={c.name}>{c.name}</SelectItem>)}</SelectContent>
                  </Select>
                ) : <div className="hidden sm:block" />}
              </div>
              <Textarea value={f.message} onChange={(e) => set("message", e.target.value)} placeholder={owner ? "Parlez-nous de votre logement (type, nombre de chambres, disponibilités…) *" : "Votre message *"} className="mt-3 min-h-[160px] rounded-2xl text-base" data-testid="contact-message" />
              <div className="mt-4"><ConsentCheck checked={consent} onChange={setConsent} testid="contact-consent" /></div>
              <button type="submit" disabled={sending} data-testid="contact-submit" className="mt-6 disabled:opacity-60 group inline-flex items-center gap-2 rounded-full bg-[#13261c] hover:bg-[#2a4636] text-white px-8 py-4 font-bold transition-colors">
                {sending ? "Envoi…" : "Envoyer"} <Send className="w-4 h-4 transition-transform group-hover:translate-x-1" />
              </button>
            </form>
          </Reveal>

          <Reveal delay={100} className="lg:col-span-5 order-1 lg:order-2 space-y-4">
            <div className="rounded-[28px] bg-[#13261c] text-[#faf7f0] p-7 relative overflow-hidden grain" data-testid="contact-hours">
              <h3 className="font-logo font-black tracking-[-0.025em] text-2xl">Horaires</h3>
              <div className="mt-5 space-y-4">
                <div className="flex gap-3"><Clock className="w-5 h-5 text-[#d9895a] shrink-0 mt-0.5" /><div><p className="font-bold">Conciergerie, téléphone & WhatsApp</p><p className="text-[#faf7f0]/75">{CONTACT.hours}</p></div></div>
                <div className="flex gap-3"><Headphones className="w-5 h-5 text-[#d9895a] shrink-0 mt-0.5" /><div><p className="font-bold">Service propriétaire (formule Premium)</p><p className="text-[#faf7f0]/75">{CONTACT.ownerHours}</p></div></div>
                <div className="flex gap-3"><Phone className="w-5 h-5 text-[#d9895a] shrink-0 mt-0.5" /><div><p className="font-bold">Astreinte urgences voyageurs</p><p className="text-[#faf7f0]/75">7j/7, jusqu'à 23h</p></div></div>
              </div>
            </div>
            <div className="rounded-[28px] bg-white ring-1 ring-[#e6dcc8] overflow-hidden" data-testid="contact-zone">
              <div className="p-7 pb-5">
                <h3 className="font-logo font-black tracking-[-0.025em] text-2xl text-[#13261c] flex items-center gap-2"><MapPin className="w-5 h-5 text-[#c4703f]" />Secteur d'intervention</h3>
                <p className="mt-2 text-[#5a6158]">{CITIES.length} communes sur la Côte d'Émeraude et la baie, de Saint-Cast-le-Guildo à Pontorson.</p>
                <Link to="/villes" className="mt-3 inline-flex items-center gap-1.5 font-bold text-[#c4703f] hover:text-[#13261c] transition-colors" data-testid="contact-cities-link">Voir toutes nos villes <ArrowUpRight className="w-4 h-4" /></Link>
              </div>
              <SectorMap className="" testid="contact-map" />
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
